import React from 'react';
import { Link } from 'react-router-dom';
import ParrotBrandMark from '../components/ParrotBrandMark';

const APP_STORE_URL =
  'https://apps.apple.com/us/app/parrot-speech-improvement/id1457391626';
const CONTACT_EMAIL = 'hello@speakparrot.com';

type TutorialAccent = 'blue' | 'black' | 'coral' | 'green';

type TutorialScreen = {
  id: string;
  eyebrow: string;
  title: string;
  body: string;
  accent: TutorialAccent;
};

const quickStartSteps = [
  {
    number: '01',
    title: 'Open the app and tap Record',
    body: 'Parrot is built around one short 60-second practice speech. Start from the Practice tab, then choose a category that matches what you want to improve today.',
  },
  {
    number: '02',
    title: 'Read the prompt and Word of the Day',
    body: 'You get a speaking topic plus one vocabulary word. You can use the word naturally for bonus credit, or ignore it when the prompt needs your full focus.',
  },
  {
    number: '03',
    title: 'Speak for 60 seconds',
    body: 'The count-in gives you a clean start. Keep talking until time runs out, even if you need to pivot, add an example, or restate your point.',
  },
  {
    number: '04',
    title: 'Review your score and next move',
    body: 'Parrot gives you a score, transcript, coaching notes, and a breakdown across pace, confidence, flow, clarity, structure, relevance, and WOTD usage.',
  },
];

const tutorialScreens: TutorialScreen[] = [
  {
    id: 'today',
    eyebrow: 'Practice',
    title: 'Start From Today',
    body: 'See your streak, level, Word of the Day, and recent recordings before starting the next speech.',
    accent: 'blue',
  },
  {
    id: 'picker',
    eyebrow: 'Topics',
    title: 'Pick A Category',
    body: 'Choose Career, Storytelling, Introspection, Persuasion, or another focus area.',
    accent: 'black',
  },
  {
    id: 'countin',
    eyebrow: 'Ready',
    title: 'Use The Count-In',
    body: 'Take the three seconds seriously. Breathe, pick your first sentence, and begin cleanly.',
    accent: 'black',
  },
  {
    id: 'record',
    eyebrow: 'Live',
    title: 'Fill The Minute',
    body: 'The timer, waveform, and live transcript keep you oriented without turning practice into a test.',
    accent: 'coral',
  },
  {
    id: 'score',
    eyebrow: 'Feedback',
    title: 'Read The Score Card',
    body: 'Use the top note for your main takeaway, then open the details when you want the full coaching pass.',
    accent: 'green',
  },
  {
    id: 'duel',
    eyebrow: 'Social',
    title: 'Try A Face-Off',
    body: 'Invite a friend into the same topic and Word of the Day. Both speeches are scored, then compared.',
    accent: 'blue',
  },
];

const scoringRows = [
  ['Pace', 'Words per minute and whether your delivery felt rushed, slow, or controlled.'],
  ['Confidence', 'Filler density, weak phrasing, strong language, delivery energy, and pause control.'],
  ['Flow', 'How much of the 60 seconds you filled with steady, connected speech.'],
  ['Clarity', 'Whether your listener could follow the point from beginning to end.'],
  ['Structure', 'How well you gave the speech a hook, point, example, and payoff.'],
  ['Relevance', 'How tightly you stayed with the prompt instead of drifting.'],
  ['WOTD usage', 'Whether the Word of the Day was used accurately and naturally.'],
];

const practicePlans = [
  {
    title: 'First Day',
    items: [
      'Record three speeches in three different categories.',
      'Ignore the score on the first one; use it as a baseline.',
      'On the second and third practice speeches, focus only on finishing the full minute.',
    ],
  },
  {
    title: 'One Week',
    items: [
      'Practice once per day in the same time window.',
      'Pick one recurring focus: fewer fillers, better opening, stronger ending, or calmer pace.',
      'Review your transcripts every few days and look for repeated habits.',
    ],
  },
  {
    title: 'Before A Real Talk',
    items: [
      'Choose the category closest to your situation: interview, pitch, toast, update, or story.',
      'Record two warm-up speeches with random prompts to loosen up.',
      'Record one final practice speech using the structure you want to bring into the real moment.',
    ],
  },
];

const faqItems = [
  {
    question: 'What is Parrot?',
    answer:
      'Parrot is a 60-second public-speaking trainer. It gives you a prompt, listens to your speech, transcribes it, scores it, and gives practical coaching so you can improve one timed speech at a time.',
  },
  {
    question: 'Do I need to prepare before recording?',
    answer:
      'No. Parrot is designed for impromptu practice. If you want a smoother recording, take the count-in to decide your first sentence and one example you can use in the middle.',
  },
  {
    question: 'What should I do if I go blank?',
    answer:
      'Say where you are in the thought, then continue. A useful line is, "The point I am trying to make is..." Parrot rewards recovery because real speaking is full of recovery.',
  },
  {
    question: 'How does the Word of the Day work?',
    answer:
      'Each day has one featured word with a definition and example. You can hear it, practice using it, and earn credit when you work it naturally into a speech.',
  },
  {
    question: 'Why did my score feel lower than expected?',
    answer:
      'A lower score usually means one of three things: the speech was short, the pace was hard to follow, or the answer drifted away from the prompt. Open the score details for the exact reason.',
  },
  {
    question: 'Can I delete a recording?',
    answer:
      'Yes. Open the recording detail and use the delete control. Deleting removes it from your visible history and progress calculations tied to that saved attempt.',
  },
  {
    question: 'Are my speeches public?',
    answer:
      'No. Recordings are private by default. Nothing posts publicly unless you deliberately use a share, challenge, or face-off feature that says another person will see it.',
  },
  {
    question: 'What happens if my score is still processing?',
    answer:
      'Stay on the result screen or come back later. If processing takes longer than normal, Parrot will still keep the recording and show the score when analysis finishes.',
  },
  {
    question: 'How do subscriptions and trials work?',
    answer:
      'Parrot subscriptions are managed by the checkout provider you used. App Store trials and billing live under your Apple ID; web offers and web subscriptions are processed through Stripe and are attached to the Parrot account email used for checkout.',
  },
  {
    question: 'How do I get help from a person?',
    answer:
      `Email ${CONTACT_EMAIL}. Include the email on your Parrot account, your device model, what you were trying to do, and any error message you saw.`,
  },
];

const troubleshootingItems = [
  {
    title: 'Microphone is not recording',
    body: 'Open iPhone Settings, find Parrot, and make sure Microphone and Speech Recognition are enabled. Then close and reopen the app before trying again.',
  },
  {
    title: 'Transcript looks incomplete',
    body: 'Speak close enough for the microphone to capture you cleanly, avoid covering the bottom of the phone, and record in a quieter room when possible.',
  },
  {
    title: 'Prompt or Word of the Day will not load',
    body: 'Check your connection, pull to refresh when available, and try another category. If it keeps happening, send us the category name and time it occurred.',
  },
  {
    title: 'Purchase is not recognized',
    body: 'For App Store purchases, use Restore Purchases with the same Apple ID that started the trial or subscription. For web offers, sign in with the same Parrot account email used during checkout.',
  },
];

const tutorialAccentClasses: Record<TutorialAccent, string> = {
  blue: 'bg-parrot-blue',
  black: 'bg-parrot-black',
  coral: 'bg-parrot-coral',
  green: 'bg-[#47B86D]',
};

const Help = () => (
  <div className="min-h-screen bg-parrot-yellow text-parrot-black">
    <header className="px-6 sm:px-16 pt-8 pb-5">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" aria-label="Back to Parrot home">
          <ParrotBrandMark className="h-12 w-12" />
          <span className="text-sm font-bold tracking-[0.22em]">PARROT</span>
        </Link>

        <nav className="hidden items-center gap-5 text-sm font-semibold sm:flex">
          <a href="#tutorial" className="hover:text-parrot-blue transition-colors">
            Tutorial
          </a>
          <a href="#faq" className="hover:text-parrot-blue transition-colors">
            FAQ
          </a>
          <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-parrot-blue transition-colors">
            Contact
          </a>
        </nav>
      </div>
    </header>

    <main>
      <section className="px-6 sm:px-16 pb-16 pt-8 sm:pt-14">
        <div className="mx-auto grid max-w-7xl items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-parrot-black/60">
              Help Center
            </p>
            <h1 className="mt-4 max-w-4xl text-[52px] font-black leading-[0.92] tracking-tight sm:text-[80px] lg:text-[104px]">
              Find your voice with Parrot.
            </h1>
            <p className="mt-8 max-w-2xl text-lg font-semibold leading-8 text-parrot-black/80 sm:text-xl">
              A detailed guide to the 60-second practice loop, the score card,
              Word of the Day, streaks, challenges, face-offs, privacy, and the
              fixes that solve the most common support questions.
            </p>

            <div className="mt-9 flex flex-wrap gap-3">
              <a
                href="#quick-start"
                className="inline-flex items-center gap-2 rounded-full bg-parrot-black px-5 py-3 text-sm font-black uppercase tracking-[0.16em] text-white transition-transform hover:scale-[1.02]"
              >
                Start Here
                <ArrowRightIcon className="h-4 w-4" />
              </a>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-parrot-black/20 bg-white px-5 py-3 text-sm font-black uppercase tracking-[0.16em] text-parrot-black transition-colors hover:border-parrot-blue hover:text-parrot-blue"
              >
                <AppleLogo className="h-4 w-4" />
                App Store
              </a>
            </div>
          </div>

          <HeroStack />
        </div>
      </section>

      <section id="quick-start" className="bg-white px-6 py-16 sm:px-16">
        <div className="mx-auto max-w-7xl">
          <SectionIntro
            label="Quick Start"
            title="Your first complete practice speech takes less than two minutes."
            body="The app is not asking for a perfect speech. It is asking for one honest minute, scored clearly enough that the next minute gets easier."
          />

          <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            {quickStartSteps.map((step) => (
              <article
                key={step.number}
                className="rounded-[16px] border border-black/10 bg-[#F7F7F4] p-6"
              >
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-parrot-blue text-sm font-black text-white">
                  {step.number}
                </div>
                <h3 className="mt-6 text-xl font-black leading-tight">{step.title}</h3>
                <p className="mt-3 text-sm leading-6 text-parrot-black/70">{step.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="tutorial" className="px-6 py-16 sm:px-16">
        <div className="mx-auto max-w-7xl">
          <SectionIntro
            label="Tutorial Screens"
            title="A guided tour of the app flow."
            body="These tutorial panels mirror the main Parrot surfaces, from the daily practice dashboard through the score reveal."
          />

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {tutorialScreens.map((screen) => (
              <TutorialCard key={screen.id} screen={screen} />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-parrot-black px-6 py-16 text-white sm:px-16">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-white/40">
              Score Card
            </p>
            <h2 className="mt-4 text-4xl font-black leading-tight sm:text-5xl">
              What Parrot is listening for.
            </h2>
            <p className="mt-5 text-base leading-7 text-white/70">
              Scores are meant to show the shape of a speech, not shame the
              speaker. Treat each dimension as a practice signal.
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {scoringRows.map(([label, description]) => (
              <article key={label} className="rounded-[16px] bg-white/10 p-5">
                <h3 className="text-lg font-black">{label}</h3>
                <p className="mt-2 text-sm leading-6 text-white/70">{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white px-6 py-16 sm:px-16">
        <div className="mx-auto max-w-7xl">
          <SectionIntro
            label="Practice Plans"
            title="Use Parrot differently depending on the moment."
            body="A fast warm-up, a daily habit, and a real-world preparation session should not feel identical. Pick the plan that matches your day."
          />

          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {practicePlans.map((plan) => (
              <article key={plan.title} className="rounded-[16px] border border-black/10 p-6">
                <h3 className="text-2xl font-black">{plan.title}</h3>
                <ul className="mt-5 space-y-4">
                  {plan.items.map((item) => (
                    <li key={item} className="flex gap-3 text-sm leading-6 text-parrot-black/70">
                      <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-parrot-coral" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 sm:px-16">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.8fr_1.2fr]">
          <div>
            <SectionIntro
              label="Troubleshooting"
              title="Fast fixes for common issues."
              body="Most recording problems come from permissions, audio quality, or a stale purchase state."
            />
          </div>

          <div className="grid gap-4">
            {troubleshootingItems.map((item) => (
              <article key={item.title} className="rounded-[16px] bg-white p-6">
                <h3 className="text-xl font-black">{item.title}</h3>
                <p className="mt-2 text-sm leading-6 text-parrot-black/70">{item.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="faq" className="bg-white px-6 py-16 sm:px-16">
        <div className="mx-auto max-w-4xl">
          <SectionIntro
            label="FAQ"
            title="Frequently asked questions."
            body="Open any question for a direct answer. When something still feels stuck, email us and we will help."
          />

          <div className="mt-10 divide-y divide-black/10 rounded-[16px] border border-black/10">
            {faqItems.map((item) => (
              <details key={item.question} className="group p-5 open:bg-[#F7F7F4] first:rounded-t-[16px] last:rounded-b-[16px]">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left text-lg font-black">
                  <span>{item.question}</span>
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-parrot-black text-sm text-white group-open:bg-parrot-blue">
                    +
                  </span>
                </summary>
                <p className="mt-4 max-w-3xl text-sm leading-6 text-parrot-black/70">
                  {item.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 sm:px-16">
        <div className="mx-auto max-w-7xl rounded-[24px] bg-parrot-black p-8 text-white sm:p-10 lg:flex lg:items-center lg:justify-between">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-white/40">
              Still Need Help?
            </p>
            <h2 className="mt-3 text-3xl font-black sm:text-5xl">
              Send us the details.
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-white/70">
              Include your Parrot account email, iPhone model, what you tapped,
              and the exact message you saw. Screenshots help when you have one.
            </p>
          </div>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-parrot-coral px-5 py-3 text-sm font-black uppercase tracking-[0.16em] text-white lg:mt-0"
          >
            Email Support
            <ArrowRightIcon className="h-4 w-4" />
          </a>
        </div>
      </section>
    </main>

    <footer className="border-t border-parrot-black/10 px-6 py-6 sm:px-16">
      <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 sm:flex-row">
        <p className="text-sm text-parrot-black/70">
          © {new Date().getFullYear()} Parrot. All rights reserved.
        </p>
        <div className="flex items-center gap-6">
          <Link to="/legal" className="text-sm text-parrot-black/80 hover:text-parrot-blue">
            Legal
          </Link>
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-sm text-parrot-black/80 hover:text-parrot-blue">
            Contact
          </a>
        </div>
      </div>
    </footer>
  </div>
);

type SectionIntroProps = {
  label: string;
  title: string;
  body: string;
};

const SectionIntro: React.FC<SectionIntroProps> = ({ label, title, body }) => (
  <div className="max-w-3xl">
    <p className="text-[11px] font-black uppercase tracking-[0.24em] text-parrot-black/50">
      {label}
    </p>
    <h2 className="mt-3 text-4xl font-black leading-tight sm:text-5xl">{title}</h2>
    <p className="mt-4 text-base leading-7 text-parrot-black/70">{body}</p>
  </div>
);

const HeroStack = () => (
  <div className="relative mx-auto w-full max-w-[520px] py-6">
    <div className="relative grid grid-cols-[0.85fr_1fr] items-center gap-4">
      <MiniPhone screen="today" className="translate-y-8" />
      <MiniPhone screen="score" featured />
    </div>
  </div>
);

type TutorialCardProps = {
  screen: TutorialScreen;
};

const TutorialCard: React.FC<TutorialCardProps> = ({ screen }) => (
  <article className="relative grid gap-5 rounded-[20px] bg-white p-5 pt-8 md:grid-cols-[0.88fr_1fr] lg:block">
    <div className={`absolute left-5 right-5 top-5 h-1.5 rounded-full ${tutorialAccentClasses[screen.accent]}`} />
    <div className="mx-auto w-full max-w-[230px]">
      <MiniPhone screen={screen.id} />
    </div>
    <div className="lg:mt-5">
      <p className="text-[10px] font-black uppercase tracking-[0.22em] text-parrot-black/40">
        {screen.eyebrow}
      </p>
      <h3 className="mt-2 text-2xl font-black">{screen.title}</h3>
      <p className="mt-3 text-sm leading-6 text-parrot-black/70">{screen.body}</p>
    </div>
  </article>
);

type MiniPhoneProps = {
  screen: string;
  featured?: boolean;
  className?: string;
};

const MiniPhone: React.FC<MiniPhoneProps> = ({ screen, featured = false, className = '' }) => (
  <div className={`relative ${className}`}>
    <div className={`rounded-[36px] bg-parrot-black p-2 ${featured ? 'shadow-2xl' : ''}`}>
      <div
        className="relative overflow-hidden rounded-[29px] bg-parrot-yellow px-4 py-5"
        style={{ aspectRatio: '9 / 19.5' }}
      >
        <div className="absolute left-1/2 top-2 h-5 w-20 -translate-x-1/2 rounded-full bg-parrot-black" />
        <PhoneScreen screen={screen} />
      </div>
    </div>
  </div>
);

const PhoneScreen: React.FC<{ screen: string }> = ({ screen }) => {
  switch (screen) {
    case 'picker':
      return <PickerScreen />;
    case 'countin':
      return <CountInScreen />;
    case 'record':
      return <RecordingScreen />;
    case 'score':
      return <ScoreScreen />;
    case 'duel':
      return <DuelScreen />;
    default:
      return <TodayScreen />;
  }
};

const TodayScreen = () => (
  <div className="flex h-full flex-col pt-9">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-[9px] font-black uppercase tracking-[0.18em] text-parrot-black/40">
          Today
        </p>
        <h4 className="mt-1 text-lg font-black leading-none">Practice</h4>
      </div>
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-parrot-black">
        <MicIcon className="h-4 w-4 text-parrot-yellow" />
      </div>
    </div>
    <div className="mt-5 rounded-[16px] bg-white p-4">
      <p className="text-[9px] font-black uppercase tracking-[0.18em] text-parrot-black/40">
        Word of the Day
      </p>
      <h5 className="mt-2 text-xl font-black text-parrot-coral">Apposite</h5>
      <p className="mt-2 text-[11px] leading-4 text-parrot-black/60">
        Exactly right for the situation.
      </p>
    </div>
    <div className="mt-4 grid grid-cols-3 gap-2">
      {[
        ['12', 'Speeches'],
        ['4', 'Streak'],
        ['Budgie', 'Level'],
      ].map(([value, label]) => (
        <div key={label} className="rounded-[12px] bg-white/70 p-2 text-center">
          <p className="text-sm font-black">{value}</p>
          <p className="mt-1 text-[8px] font-bold uppercase tracking-[0.12em] text-parrot-black/40">
            {label}
          </p>
        </div>
      ))}
    </div>
    <div className="mt-auto rounded-full bg-parrot-coral px-4 py-3 text-center text-xs font-black uppercase tracking-[0.16em] text-white">
      Record
    </div>
  </div>
);

const PickerScreen = () => (
  <div className="flex h-full flex-col pt-9">
    <p className="text-[9px] font-black uppercase tracking-[0.18em] text-parrot-black/40">
      Pick A Lane
    </p>
    <h4 className="mt-2 text-xl font-black leading-tight">What are we practicing?</h4>
    <div className="mt-5 space-y-2">
      {['Career', 'Storytelling', 'Persuasion', 'Introspection', 'Toasts'].map((label, index) => (
        <div
          key={label}
          className={`rounded-full px-4 py-3 text-xs font-black ${
            index === 1 ? 'bg-parrot-blue text-white' : 'bg-white text-parrot-black'
          }`}
        >
          {label}
        </div>
      ))}
    </div>
    <div className="mt-auto rounded-[16px] bg-white p-3">
      <p className="text-[10px] leading-4 text-parrot-black/60">
        Pick the topic closest to the real room you want to handle better.
      </p>
    </div>
  </div>
);

const CountInScreen = () => (
  <div className="absolute inset-0 flex flex-col items-center justify-center bg-parrot-black px-5 text-center text-white">
    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-white/40">
      Starting
    </p>
    <div className="mt-6 flex h-28 w-28 items-center justify-center rounded-full bg-parrot-yellow text-6xl font-black text-parrot-black">
      3
    </div>
    <p className="mt-6 text-sm font-semibold leading-5 text-white/70">
      First sentence ready. Breathe once.
    </p>
  </div>
);

const RecordingScreen = () => (
  <div className="flex h-full flex-col pt-9 text-center">
    <p className="text-sm font-black text-parrot-blue">42s left</p>
    <h4 className="mx-auto mt-5 max-w-[190px] text-lg font-black leading-tight text-parrot-blue">
      Tell us about a time you changed your mind.
    </h4>
    <div className="my-auto">
      <Waveform />
    </div>
    <div className="rounded-[16px] bg-white p-3 text-left">
      <p className="text-[10px] font-bold leading-4 text-parrot-black/60">
        I used to think confidence meant never hesitating, but now I think it
        means recovering quickly...
      </p>
    </div>
  </div>
);

const ScoreScreen = () => (
  <div className="flex h-full flex-col pt-9">
    <p className="text-center text-[9px] font-black uppercase tracking-[0.2em] text-parrot-black/40">
      Detailed Score
    </p>
    <div className="mt-4 text-center">
      <p className="text-6xl font-black leading-none text-parrot-black">87</p>
      <p className="mx-auto mt-2 w-max rounded-full bg-[#47B86D] px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-white">
        Strong Speech
      </p>
    </div>
    <div className="mt-5 rounded-[16px] bg-white p-4">
      <p className="text-[10px] font-black uppercase tracking-[0.18em] text-parrot-black/40">
        Coach Note
      </p>
      <p className="mt-2 text-[11px] font-semibold leading-4 text-parrot-black/70">
        Your opening was clear. Add one sharper ending sentence next time.
      </p>
    </div>
    <div className="mt-3 space-y-2">
      {[
        ['Pace', '91'],
        ['Confidence', '84'],
        ['Structure', '79'],
      ].map(([label, value]) => (
        <div key={label} className="flex items-center justify-between rounded-full bg-white/75 px-3 py-2">
          <span className="text-[10px] font-bold">{label}</span>
          <span className="text-[10px] font-black text-parrot-blue">{value}</span>
        </div>
      ))}
    </div>
  </div>
);

const DuelScreen = () => (
  <div className="flex h-full flex-col pt-9">
    <p className="text-[9px] font-black uppercase tracking-[0.18em] text-parrot-black/40">
      Face-Off
    </p>
    <h4 className="mt-2 text-xl font-black leading-tight">Same prompt. Same word.</h4>
    <div className="mt-5 grid grid-cols-2 gap-2">
      <div className="rounded-[16px] bg-white p-3 text-center">
        <div className="mx-auto h-10 w-10 rounded-full bg-parrot-blue" />
        <p className="mt-2 text-[10px] font-black">You</p>
        <p className="text-xl font-black">87</p>
      </div>
      <div className="rounded-[16px] bg-white p-3 text-center">
        <div className="mx-auto h-10 w-10 rounded-full bg-parrot-coral" />
        <p className="mt-2 text-[10px] font-black">Friend</p>
        <p className="text-xl font-black">82</p>
      </div>
    </div>
    <div className="mt-auto rounded-[16px] bg-parrot-black p-4 text-white">
      <p className="text-[10px] font-bold leading-4 text-white/75">
        You won on structure. They had the stronger closing line.
      </p>
    </div>
  </div>
);

const Waveform = () => (
  <svg viewBox="0 0 220 120" className="mx-auto w-full" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 60 C 22 20, 40 20, 58 60 S 94 100, 112 60 148 20, 166 60 202 100, 216 60"
      stroke="#FF6B47"
      strokeWidth="5"
      strokeLinecap="round"
    />
    <path
      d="M4 60 C 28 42, 42 42, 66 60 S 104 78, 128 60 168 42, 192 60 208 78, 216 60"
      stroke="#4A90E2"
      strokeOpacity="0.55"
      strokeWidth="4"
      strokeLinecap="round"
    />
  </svg>
);

const ArrowRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path d="M4 10h12m0 0-4.5-4.5M16 10l-4.5 4.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MicIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path d="M10 2a3 3 0 0 0-3 3v5a3 3 0 1 0 6 0V5a3 3 0 0 0-3-3Z" />
    <path d="M5 9.5a1 1 0 0 1 1 1 4 4 0 0 0 8 0 1 1 0 1 1 2 0 6 6 0 0 1-5 5.92V18h2a1 1 0 1 1 0 2H7a1 1 0 1 1 0-2h2v-1.58a6 6 0 0 1-5-5.92 1 1 0 0 1 1-1Z" />
  </svg>
);

const AppleLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 384 512" fill="currentColor" aria-hidden="true">
    <path d="M318.7 268.7c-.2-37 16.6-65 50.5-85.6-19-27.2-47.5-42.2-85.2-45.1-35.7-2.8-74.7 20.8-89 20.8-15.1 0-49.6-19.8-77.4-19.8C75.6 140.8 8 184.6 8 273.8 8 313 14.5 353.4 27.6 395.6 45 451.7 100.2 504 132.9 502.9c20-1 34.2-13.7 60.4-13.7 25.4 0 38.5 13.7 60.8 13.7 33 .5 83.1-46.8 100.6-102.8-93.7-44.3-86-127.7-66.7-156.5zm-65.4-205.1C291.6 21.3 286.6 0 281.4 0c-5.8 0-22.9 11.4-37.4 25.3-15.1 14.5-26.6 36-21.4 56.7 16.6 1.2 35.4-9.5 50.7-23z" />
  </svg>
);

export default Help;
